import { TASK_KINDS, TASK_PRIORITIES } from "@tlcrm/shared";

import { useCreateTask, useUsers } from "../api/hooks";
import { useMe } from "../auth/session";
import { FieldBox, Modal, Select } from "../components/ui";
import { useToast } from "../components/Toasts";
import { useForm } from "./useForm";

/** `<input type="datetime-local">` speaks local wall time; the API speaks instants. */
function toInstant(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function NewTaskModal({
  onClose,
  about,
}: {
  onClose: () => void;
  /** What the task is about, when it is made from a record. */
  about?: {
    contactId?: string;
    leadId?: string;
    policyId?: string;
    label?: string;
  };
}) {
  const { me } = useMe();
  const toast = useToast();
  const users = useUsers();
  const create = useCreateTask();
  const f = useForm({
    subject: "",
    kind: "call_back" as (typeof TASK_KINDS)[number][0],
    priority: "normal" as (typeof TASK_PRIORITIES)[number][0],
    dueAt: "",
    assigneeId: me.user.id,
    description: "",
  });
  const v = f.values;

  const submit = async () => {
    try {
      await create.mutateAsync({
        subject: v.subject,
        kind: v.kind,
        priority: v.priority,
        dueAt: toInstant(v.dueAt),
        assigneeId: v.assigneeId || null,
        description: v.description,
        contactId: about?.contactId ?? null,
        leadId: about?.leadId ?? null,
        policyId: about?.policyId ?? null,
      });
      toast("Task added.");
      onClose();
    } catch (err) {
      f.fail(err);
    }
  };

  return (
    <Modal
      title={about?.label ? `New task for ${about.label}` : "New task"}
      onClose={onClose}
      footer={
        <>
          {f.formError && (
            <span className="left" style={{ color: "var(--brick)" }}>
              {f.formError}
            </span>
          )}
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={() => void submit()}
            disabled={create.isPending}
          >
            {create.isPending ? "Saving…" : "Add task"}
          </button>
        </>
      }
    >
      <form
        className="form-grid"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <FieldBox label="What needs doing" htmlFor="nt-subject" error={f.errors.subject} full>
          <input
            id="nt-subject"
            placeholder="Call back with the illustration"
            {...f.text("subject")}
          />
        </FieldBox>
        <FieldBox label="Due" htmlFor="nt-due" error={f.errors.dueAt}>
          <input id="nt-due" type="datetime-local" {...f.text("dueAt")} />
        </FieldBox>
        <FieldBox label="Assigned to" htmlFor="nt-assignee">
          <Select
            id="nt-assignee"
            value={v.assigneeId}
            onChange={f.set("assigneeId")}
            options={(users.data ?? []).map(
              (u) => [u.id, u.id === me.user.id ? `${u.fullName} (me)` : u.fullName] as const,
            )}
            empty="Nobody yet"
          />
        </FieldBox>
        <FieldBox label="Type" htmlFor="nt-kind">
          <Select
            id="nt-kind"
            value={v.kind}
            onChange={(x) => f.set("kind")(x || "follow_up")}
            options={TASK_KINDS}
          />
        </FieldBox>
        <FieldBox label="Priority" htmlFor="nt-priority">
          <Select
            id="nt-priority"
            value={v.priority}
            onChange={(x) => f.set("priority")(x || "normal")}
            options={TASK_PRIORITIES}
          />
        </FieldBox>
        <FieldBox label="Notes" htmlFor="nt-desc" full>
          <textarea id="nt-desc" rows={3} {...f.text("description")} />
        </FieldBox>
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
